/**
 * SystemSettings Component
 * Super Admin interface for managing system-wide configuration
 * Allows editing Segment Hub URLs, Video Tour URL, and Performance Settings
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import './SystemSettings.css';
import { API_BASE_URL } from '../services/ApiService';

const SystemSettings = () => {
  const { token } = useAuth();
  const [settings, setSettings] = useState([]);
  const [lightweightMode, setLightweightMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState('');

  // Watch Tutorial state
  const [watchTutorial, setWatchTutorial] = useState({ url: '', text: '', button_text: '', active: true });
  const [editingWatchTutorial, setEditingWatchTutorial] = useState(false);
  const [watchTutorialForm, setWatchTutorialForm] = useState({ url: '', text: '', button_text: '', active: true });
  const [videosPopupActive, setVideosPopupActive] = useState(true);

  // Optional Videos state
  const EMPTY_VIDEO = { title: '', button_text: '', extra_text: '', url: '', end_date: '', active: true };
  const [optionalVideos, setOptionalVideos] = useState([]);
  const [showAddVideo, setShowAddVideo] = useState(false);
  const [newVideoForm, setNewVideoForm] = useState({ ...EMPTY_VIDEO });
  const [editVideoIdx, setEditVideoIdx] = useState(null);
  const [editVideoForm, setEditVideoForm] = useState({ ...EMPTY_VIDEO });

  // Homepage Logo state
  const [currentLogoUrl, setCurrentLogoUrl] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoUrlInput, setLogoUrlInput] = useState('');
  const [logoMode, setLogoMode] = useState('upload'); // 'upload' or 'url'
  const [logoActive, setLogoActive] = useState(true);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoHeight, setLogoHeight] = useState(54);
  const [logoMaxWidth, setLogoMaxWidth] = useState(280);
  const [logoSizeSaving, setLogoSizeSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchLightweightMode();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch settings');
      }

      const data = await response.json();
      const raw = data.data || [];
      setSettings(raw);

      // Extract dedicated settings
      const urlSetting = raw.find(s => s.key === 'watch_tutorial_url');
      const textSetting = raw.find(s => s.key === 'watch_tutorial_text');
      const btnTextSetting = raw.find(s => s.key === 'watch_tutorial_button_text');
      const activeSetting = raw.find(s => s.key === 'watch_tutorial_active');
      const popupActiveSetting = raw.find(s => s.key === 'videos_popup_active');
      const videosSetting = raw.find(s => s.key === 'optional_videos');
      const wt = {
        url: urlSetting?.value || '',
        text: textSetting?.value || '',
        button_text: btnTextSetting?.value || '',
        active: activeSetting ? activeSetting.value !== 'false' : true,
      };
      setWatchTutorial(wt);
      setVideosPopupActive(popupActiveSetting ? popupActiveSetting.value !== 'false' : true);
      if (videosSetting) {
        try { setOptionalVideos(JSON.parse(videosSetting.value || '[]')); } catch { /* ignore */ }
      }
      
      // Extract logo settings
      const logoSetting = raw.find(s => s.key === 'homepage_logo_url');
      const logoActiveSetting = raw.find(s => s.key === 'homepage_logo_active');
      if (logoSetting?.value) {
        let logoUrl = logoSetting.value;
        // If the URL is a relative /static/ path, make it absolute via the API base
        if (logoUrl.startsWith('/static/')) {
          const apiBase = API_BASE_URL.replace(/\/api\/?$/, '');
          logoUrl = apiBase ? `${apiBase}${logoUrl}` : logoUrl;
        }
        setCurrentLogoUrl(logoUrl);
      }
      setLogoActive(logoActiveSetting ? logoActiveSetting.value !== 'false' : true);
      const logoHeightSetting = raw.find(s => s.key === 'homepage_logo_height');
      const logoMaxWidthSetting = raw.find(s => s.key === 'homepage_logo_max_width');
      if (logoHeightSetting?.value) setLogoHeight(parseInt(logoHeightSetting.value, 10) || 54);
      if (logoMaxWidthSetting?.value) setLogoMaxWidth(parseInt(logoMaxWidthSetting.value, 10) || 280);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchLightweightMode = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/super-admin/settings/lightweight-mode`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch lightweight mode setting');
      }

      const data = await response.json();
      setLightweightMode(data.data.lightweightMode);
    } catch (err) {
      console.error('Failed to fetch lightweight mode:', err);
    }
  };

  const handleLightweightModeToggle = async () => {
    try {
      setError(null);
      const newValue = !lightweightMode;
      
      const response = await fetch(`${API_BASE_URL}/super-admin/settings/lightweight-mode`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ enabled: newValue }),
      });

      if (!response.ok) {
        throw new Error('Failed to update lightweight mode');
      }

      setLightweightMode(newValue);
      setSuccessMessage(
        newValue 
          ? '⚡ Lightweight Mode Enabled - Photos disabled for all users'
          : '📷 Lightweight Mode Disabled - Photos enabled for all users'
      );
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Failed to toggle lightweight mode:', err);
      setError(err.message);
    }
  };

  const handleEdit = (setting) => {
    setEditingKey(setting.key);
    setEditValue(setting.value);
    setSuccessMessage('');
    setError(null);
  };

  const handleCancel = () => {
    setEditingKey(null);
    setEditValue('');
  };

  const handleSave = async (key) => {
    try {
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/super-admin/settings/${key}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ value: editValue }),
      });

      if (!response.ok) {
        throw new Error('Failed to update setting');
      }

      const data = await response.json();
      
      // Update local state
      setSettings(settings.map(s => 
        s.key === key ? data.data : s
      ));
      
      setEditingKey(null);
      setEditValue('');
      setSuccessMessage(`Successfully updated ${key}`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Failed to update setting:', err);
      setError(err.message);
    }
  };

  // Generic helper to save any setting by key/value
  const saveSetting = async (key, value) => {
    const response = await fetch(`${API_BASE_URL}/super-admin/settings/${key}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ value }),
    });
    if (!response.ok) throw new Error('Failed to update setting');
    return (await response.json()).data;
  };

  // Watch Tutorial handlers
  const handleSaveWatchTutorial = async () => {
    try {
      setError(null);
      await saveSetting('watch_tutorial_url', watchTutorialForm.url);
      await saveSetting('watch_tutorial_text', watchTutorialForm.text);
      await saveSetting('watch_tutorial_button_text', watchTutorialForm.button_text);
      await saveSetting('watch_tutorial_active', String(watchTutorialForm.active));
      setWatchTutorial({ ...watchTutorialForm });
      setEditingWatchTutorial(false);
      setSuccessMessage('Watch Tutorial settings updated');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleWatchTutorialActive = async () => {
    try {
      setError(null);
      const newActive = !watchTutorial.active;
      await saveSetting('watch_tutorial_active', String(newActive));
      setWatchTutorial(prev => ({ ...prev, active: newActive }));
    } catch (err) { setError(err.message); }
  };

  const handleToggleVideosPopupActive = async () => {
    try {
      setError(null);
      const newActive = !videosPopupActive;
      await saveSetting('videos_popup_active', String(newActive));
      setVideosPopupActive(newActive);
      setSuccessMessage(newActive ? 'Videos popup enabled' : 'Videos popup disabled');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) { setError(err.message); }
  };

  // Optional Videos helpers
  const persistVideos = async (videos) => {
    await saveSetting('optional_videos', JSON.stringify(videos));
    setOptionalVideos(videos);
  };

  const handleAddVideo = async () => {
    try {
      setError(null);
      const video = {
        title: newVideoForm.title,
        button_text: newVideoForm.button_text,
        extra_text: newVideoForm.extra_text,
        url: newVideoForm.url,
        end_date: newVideoForm.end_date || null,
        active: newVideoForm.active,
      };
      await persistVideos([...optionalVideos, video]);
      setNewVideoForm({ ...EMPTY_VIDEO });
      setShowAddVideo(false);
      setSuccessMessage('Video added');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) { setError(err.message); }
  };

  const handleSaveVideo = async (idx) => {
    try {
      setError(null);
      const updated = optionalVideos.map((v, i) =>
        i === idx ? {
          title: editVideoForm.title,
          button_text: editVideoForm.button_text,
          extra_text: editVideoForm.extra_text,
          url: editVideoForm.url,
          end_date: editVideoForm.end_date || null,
          active: editVideoForm.active,
        } : v
      );
      await persistVideos(updated);
      setEditVideoIdx(null);
      setSuccessMessage('Video updated');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) { setError(err.message); }
  };

  const handleToggleVideoActive = async (idx) => {
    try {
      setError(null);
      const updated = optionalVideos.map((v, i) =>
        i === idx ? { ...v, active: !v.active } : v
      );
      await persistVideos(updated);
    } catch (err) { setError(err.message); }
  };

  const handleDeleteVideo = async (idx) => {
    if (!window.confirm('Delete this video?')) return;
    try {
      setError(null);
      await persistVideos(optionalVideos.filter((_, i) => i !== idx));
      setSuccessMessage('Video deleted');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) { setError(err.message); }
  };

  // Logo handlers
  const handleSaveLogo = async () => {
    try {
      setError(null);
      setLogoUploading(true);
      let logoUrl;
      if (logoMode === 'upload' && logoFile) {
        const formData = new FormData();
        formData.append('logo', logoFile);
        const response = await fetch(`${API_BASE_URL}/super-admin/settings/logo`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        });
        if (!response.ok) throw new Error('Failed to upload logo');
        const data = await response.json();
        logoUrl = data.data.value;
      } else if (logoMode === 'url' && logoUrlInput) {
        try { new URL(logoUrlInput); } catch { throw new Error('Invalid URL format'); }
        const response = await fetch(`${API_BASE_URL}/super-admin/settings/logo`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ url: logoUrlInput }),
        });
        if (!response.ok) throw new Error('Failed to save logo URL');
        const data = await response.json();
        logoUrl = data.data.value;
      } else {
        throw new Error('Please select a file or enter a URL');
      }
      if (logoUrl.startsWith('/static/')) {
        const apiBase = API_BASE_URL.replace(/\/api\/?$/, '');
        logoUrl = apiBase ? `${apiBase}${logoUrl}` : logoUrl;
      }
      setCurrentLogoUrl(logoUrl);
      setLogoFile(null);
      setLogoPreview(null);
      setLogoUrlInput('');
      setSuccessMessage('Logo saved successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLogoUploading(false);
    }
  };

  const handleResetLogo = async () => {
    if (!window.confirm('Remove the custom logo? The logo will be hidden from the homepage.')) return;
    try {
      setError(null);
      const response = await fetch(`${API_BASE_URL}/super-admin/settings/logo`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to reset logo');
      setCurrentLogoUrl('');
      setLogoFile(null);
      setLogoPreview(null);
      setLogoUrlInput('');
      setLogoActive(false);
      setSuccessMessage('Logo removed');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleLogoSizeSubmit = async () => {
    try {
      setError(null);
      setLogoSizeSaving(true);
      const h = Math.max(20, Math.min(120, logoHeight));
      const w = Math.max(40, Math.min(600, logoMaxWidth));
      await Promise.all([
        saveSetting('homepage_logo_height', h.toString()),
        saveSetting('homepage_logo_max_width', w.toString()),
      ]);
      setLogoHeight(h);
      setLogoMaxWidth(w);
      setSuccessMessage('Logo size saved');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLogoSizeSaving(false);
    }
  };

  const handleLogoActiveToggle = async () => {
    try {
      setError(null);
      const newValue = !logoActive;
      const response = await fetch(`${API_BASE_URL}/super-admin/settings/homepage_logo_active`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ value: newValue.toString() }),
      });
      if (!response.ok) throw new Error('Failed to update logo visibility');
      setLogoActive(newValue);
      setSuccessMessage(newValue ? 'Logo is now visible on homepage' : 'Logo is now hidden from homepage');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const DEDICATED_KEYS = new Set(['watch_tutorial_url', 'watch_tutorial_text', 'watch_tutorial_button_text', 'watch_tutorial_active', 'videos_popup_active', 'optional_videos', 'homepage_logo_url', 'homepage_logo_active', 'homepage_logo_height', 'homepage_logo_max_width']);

  const groupedSettings = settings.reduce((acc, setting) => {
    if (DEDICATED_KEYS.has(setting.key)) return acc;
    if (!acc[setting.category]) {
      acc[setting.category] = [];
    }
    acc[setting.category].push(setting);
    return acc;
  }, {});

  const categoryLabels = {
    segment_hub: 'Segment Hub URLs',
    general: 'General Settings',
  };

  // Map setting keys to display names
  const segmentDisplayNames = {
    'segment_hub_ops_sw_url': 'OPS-SW',
    'segment_hub_grow_plm_url': 'Grow@PLM',
    'segment_hub_lcs_url': 'LCS',
    'segment_hub_me_url': 'ME',
    'segment_hub_pes_url': 'PES',
    'segment_hub_sim_url': 'SIM',
    'video_tour_url': 'Video Tour',
  };

  if (loading) {
    return (
      <div className="system-settings">
        <div className="loading">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="system-settings">
      <div className="settings-header">
        <h2>⚙️ System Configuration</h2>
        <p className="settings-description">
          Manage system-wide URLs and configuration. Changes take effect immediately for all users.
        </p>
      </div>

      {successMessage && (
        <div className="success-message">
          ✓ {successMessage}
        </div>
      )}

      {error && (
        <div className="error-message">
          ✖ {error}
        </div>
      )}

      {/* Homepage Logo Section */}
      <div className="settings-category">
        <h3 className="category-title">🖼️ Homepage Logo</h3>
        <p className="settings-description" style={{ margin: '0 0 1.25rem 0', color: '#6b7280', fontSize: '14px' }}>
          Upload an image or provide a URL. The logo appears in the top-left corner of the dashboard header, left of the Segment Hub Link.
        </p>

        {/* Mode Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '1.25rem' }}>
          <button
            onClick={() => { setLogoMode('upload'); setLogoUrlInput(''); setLogoPreview(null); }}
            style={{
              padding: '6px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', border: '2px solid',
              borderColor: logoMode === 'upload' ? '#2387aa' : '#d1d5db',
              background: logoMode === 'upload' ? '#2387aa' : '#fff',
              color: logoMode === 'upload' ? '#fff' : '#374151',
            }}
          >📁 Upload Image</button>
          <button
            onClick={() => { setLogoMode('url'); setLogoFile(null); setLogoPreview(null); }}
            style={{
              padding: '6px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', border: '2px solid',
              borderColor: logoMode === 'url' ? '#2387aa' : '#d1d5db',
              background: logoMode === 'url' ? '#2387aa' : '#fff',
              color: logoMode === 'url' ? '#fff' : '#374151',
            }}
          >🔗 Use URL</button>
        </div>

        {/* Upload Mode */}
        {logoMode === 'upload' && (
          <div style={{ marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <label style={{ cursor: 'pointer' }}>
              <input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/gif,image/webp"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setLogoFile(file);
                    setLogoPreview(URL.createObjectURL(file));
                  }
                }}
              />
              <span className="btn-edit" style={{ display: 'inline-block', cursor: 'pointer' }}>📁 Choose File</span>
            </label>
            {logoFile && (
              <span style={{ fontSize: '13px', color: '#6b7280' }}>{logoFile.name}</span>
            )}
          </div>
        )}

        {/* URL Mode */}
        {logoMode === 'url' && (
          <div style={{ marginBottom: '1.25rem' }}>
            <input
              type="url"
              placeholder="https://example.com/logo.png"
              value={logoUrlInput}
              onChange={(e) => {
                setLogoUrlInput(e.target.value);
                setLogoPreview(e.target.value || null);
              }}
              style={{ width: '100%', maxWidth: '480px', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', fontFamily: 'monospace' }}
            />
          </div>
        )}

        {/* Current Logo + Preview */}
        <div style={{ display: 'flex', gap: '2rem', marginBottom: '1.25rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {currentLogoUrl && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Current Logo</span>
              <img
                src={currentLogoUrl}
                alt="Current logo"
                style={{ height: '48px', maxWidth: '200px', objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: '6px', background: '#f9fafb', padding: '4px' }}
              />
            </div>
          )}
          {logoPreview && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Preview</span>
              <img
                src={logoPreview}
                alt="Logo preview"
                style={{ height: '48px', maxWidth: '200px', objectFit: 'contain', border: '2px solid #2387aa', borderRadius: '6px', background: '#f9fafb', padding: '4px' }}
                onError={() => setLogoPreview(null)}
              />
            </div>
          )}
          {!currentLogoUrl && !logoPreview && (
            <span style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>No logo set</span>
          )}
        </div>

        {/* Size Parameters */}
        {currentLogoUrl && (
          <div style={{ marginBottom: '1.25rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '0.75rem' }}>📐 Logo Size</span>
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Height (px)</label>
                <input
                  type="number"
                  min={20}
                  max={120}
                  value={logoHeight}
                  onChange={(e) => setLogoHeight(parseInt(e.target.value, 10) || 54)}
                  style={{ width: '90px', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Max Width (px)</label>
                <input
                  type="number"
                  min={40}
                  max={600}
                  value={logoMaxWidth}
                  onChange={(e) => setLogoMaxWidth(parseInt(e.target.value, 10) || 280)}
                  style={{ width: '110px', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                />
              </div>
              <button
                className="btn-save"
                onClick={handleLogoSizeSubmit}
                disabled={logoSizeSaving}
                style={{ marginBottom: '1px' }}
              >
                {logoSizeSaving ? '⏳ Saving…' : '💾 Apply Size'}
              </button>
            </div>
            <p style={{ margin: '0.5rem 0 0', fontSize: '12px', color: '#9ca3af' }}>Height: 20–120 px · Max width: 40–600 px</p>
          </div>
        )}

        {/* Visibility Toggle */}
        {currentLogoUrl && (
          <div style={{ marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              type="checkbox"
              id="logo-active-toggle"
              checked={logoActive}
              onChange={handleLogoActiveToggle}
              style={{ cursor: 'pointer', width: '16px', height: '16px' }}
            />
            <label htmlFor="logo-active-toggle" style={{ cursor: 'pointer', fontSize: '14px', color: '#374151', fontWeight: '500' }}>
              Show logo on homepage
            </label>
            <span style={{ fontSize: '12px', color: logoActive ? '#059669' : '#9ca3af' }}>
              {logoActive ? '● Visible' : '○ Hidden'}
            </span>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            className="btn-save"
            disabled={(!logoFile && !logoUrlInput) || logoUploading}
            onClick={handleSaveLogo}
          >
            {logoUploading ? '⏳ Saving…' : '💾 Save Logo'}
          </button>
          {currentLogoUrl && (
            <button className="btn-cancel" onClick={handleResetLogo}>
              🗑️ Remove Logo
            </button>
          )}
        </div>
      </div>

      {/* Performance Settings Section */}
      <div className="settings-category performance-settings">
        <h3 className="category-title">⚡ Performance Settings</h3>
        
        <div className="performance-card">
          <div className="performance-toggle-row">
            <div className="toggle-info">
              <h4>Lightweight Mode</h4>
              <p className="toggle-description">
                Disables presenter photos on all session cards to reduce bandwidth usage during peak load.
                <br/>
                <strong>Use case:</strong> Enable before events with 1,000+ concurrent users to reduce bandwidth by 20-30x.
              </p>
              <div className="toggle-stats">
                <span className="stat-label">Status:</span>
                <span className={`stat-value ${lightweightMode ? 'enabled' : 'disabled'}`}>
                  {lightweightMode ? '⚡ ENABLED - Photos disabled' : '📷 DISABLED - Photos showing'}
                </span>
              </div>
            </div>
            
            <div className="toggle-control">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={lightweightMode}
                  onChange={handleLightweightModeToggle}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
          
          <div className="performance-impact">
            <div className="impact-metric">
              <span className="metric-label">Bandwidth Reduction:</span>
              <span className="metric-value">{lightweightMode ? '~95%' : '0%'}</span>
            </div>
            <div className="impact-metric">
              <span className="metric-label">Page Load Speed:</span>
              <span className="metric-value">{lightweightMode ? '<1s' : '3-5s'}</span>
            </div>
            <div className="impact-metric">
              <span className="metric-label">Capacity:</span>
              <span className="metric-value">{lightweightMode ? '2,000+ users' : '500-1,000 users'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* URL Settings Section */}

      {Object.entries(groupedSettings).map(([category, categorySettings]) => (
        <div key={category} className="settings-category">
          <h3 className="category-title">{categoryLabels[category] || category}</h3>
          
          <table className="settings-table">
            <thead>
              <tr>
                <th>Segment</th>
                <th>URL</th>
                <th>Actions</th>
                <th>Last Updated</th>
              </tr>
            </thead>
            <tbody>
              {categorySettings.map(setting => (
                <tr key={setting.key}>
                  <td className="segment-name">
                    {segmentDisplayNames[setting.key] || setting.key}
                  </td>
                  
                  {editingKey === setting.key ? (
                    <>
                      <td className="setting-edit-cell">
                        <input
                          type="url"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="setting-input"
                          placeholder="Enter URL"
                        />
                      </td>
                      <td className="setting-actions-cell">
                        <button 
                          onClick={() => handleSave(setting.key)}
                          className="btn-save"
                          disabled={!editValue}
                        >
                          💾 Save
                        </button>
                        <button 
                          onClick={handleCancel}
                          className="btn-cancel"
                        >
                          Cancel
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="setting-value-cell">
                        {setting.value ? (
                          <a 
                            href={setting.value} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="setting-value-link"
                          >
                            {setting.value}
                          </a>
                        ) : (
                          <span className="empty-value">Not configured</span>
                        )}
                      </td>
                      <td className="setting-actions-cell">
                        <button 
                          onClick={() => handleEdit(setting)}
                          className="btn-edit"
                        >
                          ✏️ Edit
                        </button>
                      </td>
                    </>
                  )}
                  
                  <td className="setting-meta-cell">
                    {new Date(setting.updated_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {/* Videos Popup Master Toggle */}
      <div className="settings-category">
        <h3 className="category-title">🎛️ Videos Popup</h3>
        <p className="settings-description" style={{ margin: '0 0 1rem 0', color: '#6b7280', fontSize: '14px' }}>
          Controls visibility of the entire homepage videos popup, including both Watch Tutorial and Optional Videos.
        </p>
        <div className="watch-tutorial-card">
          <div className="video-card-header" style={{ marginBottom: '0' }}>
            <span className="video-card-title">Homepage Videos Popup</span>
            <label className="active-toggle" title={videosPopupActive ? 'Click to hide the entire videos popup' : 'Click to show the videos popup'}>
              <input type="checkbox" checked={videosPopupActive} onChange={handleToggleVideosPopupActive} />
              <span className={`active-badge ${videosPopupActive ? 'active-yes' : 'active-no'}`}>
                {videosPopupActive ? 'Enabled' : 'Disabled'}
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Watch Tutorial Section */}
      <div className="settings-category">
        <h3 className="category-title">📹 Watch Tutorial</h3>
        <div className="watch-tutorial-card">
          {editingWatchTutorial ? (
            <div className="watch-tutorial-form">
              <div className="add-video-fields">
                <div className="field-group">
                  <label className="field-label">Description Text</label>
                  <input
                    type="text"
                    value={watchTutorialForm.text}
                    onChange={e => setWatchTutorialForm({ ...watchTutorialForm, text: e.target.value })}
                    className="setting-input"
                    placeholder="e.g. Watch our Learning Day tutorial..."
                  />
                </div>
                <div className="field-group">
                  <label className="field-label">Button Text</label>
                  <input
                    type="text"
                    value={watchTutorialForm.button_text}
                    onChange={e => setWatchTutorialForm({ ...watchTutorialForm, button_text: e.target.value })}
                    className="setting-input"
                    placeholder="e.g. ▶ Watch Tutorial"
                  />
                </div>
                <div className="field-group">
                  <label className="field-label">Video URL</label>
                  <input
                    type="url"
                    value={watchTutorialForm.url}
                    onChange={e => setWatchTutorialForm({ ...watchTutorialForm, url: e.target.value })}
                    className="setting-input"
                    placeholder="https://..."
                  />
                </div>
                <div className="field-group">
                  <label className="field-label">Show in Popup</label>
                  <div className="active-field">
                    <label className="radio-option">
                      <input type="radio" checked={watchTutorialForm.active === true} onChange={() => setWatchTutorialForm({ ...watchTutorialForm, active: true })} /> Yes
                    </label>
                    <label className="radio-option">
                      <input type="radio" checked={watchTutorialForm.active === false} onChange={() => setWatchTutorialForm({ ...watchTutorialForm, active: false })} /> No
                    </label>
                  </div>
                </div>
              </div>
              <div className="watch-tutorial-actions">
                <button onClick={handleSaveWatchTutorial} className="btn-save" disabled={!watchTutorialForm.url}>
                  💾 Save
                </button>
                <button onClick={() => setEditingWatchTutorial(false)} className="btn-cancel">Cancel</button>
              </div>
            </div>
          ) : (
            <div className="watch-tutorial-view">
              <div className="video-card-header" style={{ marginBottom: '12px' }}>
                <span className="video-card-title">Watch Tutorial</span>
                <label className="active-toggle" title={watchTutorial.active ? 'Click to hide from popup' : 'Click to show in popup'}>
                  <input type="checkbox" checked={watchTutorial.active} onChange={handleToggleWatchTutorialActive} />
                  <span className={`active-badge ${watchTutorial.active ? 'active-yes' : 'active-no'}`}>
                    {watchTutorial.active ? 'Active' : 'Inactive'}
                  </span>
                </label>
              </div>
              <div className="video-card-fields">
                <div className="wt-field">
                  <span className="wt-label">Description:</span>
                  <span className="wt-value">{watchTutorial.text || <em className="empty-value">Not set</em>}</span>
                </div>
                <div className="wt-field">
                  <span className="wt-label">Button Text:</span>
                  <span className="wt-value">{watchTutorial.button_text || <em className="empty-value">Not set</em>}</span>
                </div>
                <div className="wt-field">
                  <span className="wt-label">URL:</span>
                  {watchTutorial.url ? (
                    <a href={watchTutorial.url} target="_blank" rel="noopener noreferrer" className="setting-value-link">
                      {watchTutorial.url}
                    </a>
                  ) : <span className="empty-value">Not configured</span>}
                </div>
              </div>
              <div style={{ marginTop: '16px' }}>
                <button
                  onClick={() => { setWatchTutorialForm({ ...watchTutorial }); setEditingWatchTutorial(true); }}
                  className="btn-edit"
                >
                  ✏️ Edit
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Optional Videos Section */}
      <div className="settings-category">
        <h3 className="category-title">🎬 Optional Videos</h3>
        <p className="settings-description" style={{ margin: '0 0 1.5rem 0', color: '#6b7280', fontSize: '14px' }}>
          Videos appear as buttons in the homepage popup. Use the Active toggle to show or hide each video instantly.
          Set an end date to automatically hide a video after that date.
        </p>

        {optionalVideos.length > 0 && (
          <div className="video-cards" style={{ marginBottom: '1.5rem' }}>
            {optionalVideos.map((video, idx) => (
              <div key={idx} className={`video-card ${!video.active ? 'video-card-inactive' : ''}`}>
                {editVideoIdx === idx ? (
                  <div className="video-card-edit">
                    <div className="add-video-fields">
                      <div className="field-group">
                        <label className="field-label">Title <span className="field-hint-inline">(internal reference)</span></label>
                        <input type="text" value={editVideoForm.title} onChange={e => setEditVideoForm({ ...editVideoForm, title: e.target.value })} className="setting-input" placeholder="e.g. Tony's Message" />
                      </div>
                      <div className="field-group">
                        <label className="field-label">Button Text</label>
                        <input type="text" value={editVideoForm.button_text} onChange={e => setEditVideoForm({ ...editVideoForm, button_text: e.target.value })} className="setting-input" placeholder="e.g. 🎙️ Watch Tony's Message" />
                      </div>
                      <div className="field-group">
                        <label className="field-label">Show Until (optional)</label>
                        <div className="date-field">
                          <input type="date" value={editVideoForm.end_date || ''} onChange={e => setEditVideoForm({ ...editVideoForm, end_date: e.target.value })} className="setting-input" />
                          {editVideoForm.end_date && (
                            <button type="button" onClick={() => setEditVideoForm({ ...editVideoForm, end_date: '' })} className="btn-clear-date" title="Always show">✕</button>
                          )}
                        </div>
                      </div>
                      <div className="field-group" style={{ gridColumn: '1 / -1' }}>
                        <label className="field-label">Additional Text <span className="field-hint-inline">(shown above button in popup)</span></label>
                        <input type="text" value={editVideoForm.extra_text} onChange={e => setEditVideoForm({ ...editVideoForm, extra_text: e.target.value })} className="setting-input" placeholder="e.g. A special message from our VP..." />
                      </div>
                      <div className="field-group" style={{ gridColumn: '1 / -1' }}>
                        <label className="field-label">Video URL</label>
                        <input type="url" value={editVideoForm.url} onChange={e => setEditVideoForm({ ...editVideoForm, url: e.target.value })} className="setting-input" placeholder="https://..." />
                      </div>
                    </div>
                    <div className="add-video-actions">
                      <button onClick={() => handleSaveVideo(idx)} className="btn-save" disabled={!editVideoForm.url}>💾 Save</button>
                      <button onClick={() => setEditVideoIdx(null)} className="btn-cancel">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="video-card-view">
                    <div className="video-card-header">
                      <span className="video-card-title">{video.title || '(untitled)'}</span>
                      <div className="video-card-header-actions">
                        <label className="active-toggle" title={video.active !== false ? 'Click to hide from popup' : 'Click to show in popup'}>
                          <input
                            type="checkbox"
                            checked={video.active !== false}
                            onChange={() => handleToggleVideoActive(idx)}
                          />
                          <span className={`active-badge ${video.active !== false ? 'active-yes' : 'active-no'}`}>
                            {video.active !== false ? 'Active' : 'Inactive'}
                          </span>
                        </label>
                      </div>
                    </div>
                    <div className="video-card-fields">
                      <div className="video-card-field">
                        <span className="wt-label">Button Text:</span>
                        <span className="wt-value">{video.button_text || <em className="empty-value">Not set (uses title)</em>}</span>
                      </div>
                      {video.extra_text && (
                        <div className="video-card-field">
                          <span className="wt-label">Additional Text:</span>
                          <span className="wt-value">{video.extra_text}</span>
                        </div>
                      )}
                      <div className="video-card-field">
                        <span className="wt-label">URL:</span>
                        <a href={video.url} target="_blank" rel="noopener noreferrer" className="setting-value-link">{video.url}</a>
                      </div>
                      <div className="video-card-field">
                        <span className="wt-label">Show Until:</span>
                        {video.end_date
                          ? <span>{new Date(video.end_date).toLocaleDateString()}</span>
                          : <span className="always-badge">Always</span>}
                      </div>
                    </div>
                    <div className="video-card-actions">
                      <button onClick={() => {
                        setEditVideoIdx(idx);
                        setEditVideoForm({
                          title: video.title || '',
                          button_text: video.button_text || '',
                          extra_text: video.extra_text || '',
                          url: video.url || '',
                          end_date: video.end_date || '',
                          active: video.active !== false,
                        });
                      }} className="btn-edit">✏️ Edit</button>
                      <button onClick={() => handleDeleteVideo(idx)} className="btn-delete">🗑️ Delete</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {!showAddVideo ? (
          <button onClick={() => setShowAddVideo(true)} className="btn-add-video">+ Add Video</button>
        ) : (
          <div className="add-video-form">
            <h4 style={{ margin: '0 0 16px 0', color: '#2c3e50' }}>New Optional Video</h4>
            <div className="add-video-fields">
              <div className="field-group">
                <label className="field-label">Title <span className="field-hint-inline">(internal reference)</span></label>
                <input type="text" value={newVideoForm.title} onChange={e => setNewVideoForm({ ...newVideoForm, title: e.target.value })} className="setting-input" placeholder="e.g. Tony's Message" />
              </div>
              <div className="field-group">
                <label className="field-label">Button Text</label>
                <input type="text" value={newVideoForm.button_text} onChange={e => setNewVideoForm({ ...newVideoForm, button_text: e.target.value })} className="setting-input" placeholder="e.g. 🎙️ Watch Tony's Message" />
              </div>
              <div className="field-group">
                <label className="field-label">Show Until (optional)</label>
                <div className="date-field">
                  <input type="date" value={newVideoForm.end_date} onChange={e => setNewVideoForm({ ...newVideoForm, end_date: e.target.value })} className="setting-input" />
                  {newVideoForm.end_date && (
                    <button type="button" onClick={() => setNewVideoForm({ ...newVideoForm, end_date: '' })} className="btn-clear-date" title="Remove end date">✕</button>
                  )}
                </div>
                <span className="field-hint">Leave blank to always show</span>
              </div>
              <div className="field-group" style={{ gridColumn: '1 / -1' }}>
                <label className="field-label">Additional Text <span className="field-hint-inline">(shown above button in popup)</span></label>
                <input type="text" value={newVideoForm.extra_text} onChange={e => setNewVideoForm({ ...newVideoForm, extra_text: e.target.value })} className="setting-input" placeholder="e.g. A special message from our VP..." />
              </div>
              <div className="field-group" style={{ gridColumn: '1 / -1' }}>
                <label className="field-label">Video URL</label>
                <input type="url" value={newVideoForm.url} onChange={e => setNewVideoForm({ ...newVideoForm, url: e.target.value })} className="setting-input" placeholder="https://..." />
              </div>
              <div className="field-group">
                <label className="field-label">Show in Popup</label>
                <div className="active-field">
                  <label className="radio-option">
                    <input type="radio" checked={newVideoForm.active === true} onChange={() => setNewVideoForm({ ...newVideoForm, active: true })} /> Yes
                  </label>
                  <label className="radio-option">
                    <input type="radio" checked={newVideoForm.active === false} onChange={() => setNewVideoForm({ ...newVideoForm, active: false })} /> No
                  </label>
                </div>
              </div>
            </div>
            <div className="add-video-actions">
              <button onClick={handleAddVideo} className="btn-save" disabled={!newVideoForm.url}>+ Add</button>
              <button onClick={() => { setShowAddVideo(false); setNewVideoForm({ ...EMPTY_VIDEO }); }} className="btn-cancel">Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemSettings;
